<script setup lang="ts">
import type { AlertVariant } from '@/types/ui';
import { Icon } from '@iconify/vue';
/**
 * UiAlert - Design System Alert Component
 *
 * Inline alert for displaying contextual messages within the page content.
 *
 * WHEN TO USE:
 * - info: Neutral information, tips, or guidance
 * - success: Confirmation of completed actions (form submitted, saved)
 * - warning: Caution about potential issues (unsaved changes, deprecated features)
 * - error: Error messages, validation failures, system errors
 *
 * @example
 * <UiAlert variant="success" title="Saved!" message="Your changes have been saved." />
 * <UiAlert variant="error" dismissible>Something went wrong.</UiAlert>
 */
import { computed } from 'vue';
import { IconLib } from '@/assets/icon-library';

export interface UiAlertProps {
   /** Alert variant */
   variant?: AlertVariant;
   /** Title text */
   title?: string;
   /** Message text (alternative to slot) */
   message?: string;
   /** Whether the alert can be dismissed */
   dismissible?: boolean;
   /** Custom icon (overrides default variant icon) */
   icon?: string;
   /** Hide the icon */
   hideIcon?: boolean;
}

const props = withDefaults(defineProps<UiAlertProps>(), {
   variant: 'info',
   dismissible: false,
   hideIcon: false,
});

const emit = defineEmits<{
   dismiss: [];
}>();

const variantConfig = computed(() => {
   const configs: Record<AlertVariant, { bg: string; border: string; text: string; icon: string }> = {
      info: {
         bg: 'bg-blue-50',
         border: 'border-blue-200',
         text: 'text-blue-800',
         icon: IconLib.info,
      },
      success: {
         bg: 'bg-green-50',
         border: 'border-green-200',
         text: 'text-green-800',
         icon: 'ph:check-circle',
      },
      warning: {
         bg: 'bg-yellow-50',
         border: 'border-yellow-200',
         text: 'text-yellow-800',
         icon: IconLib.error.triangle,
      },
      error: {
         bg: 'bg-red-50',
         border: 'border-red-200',
         text: 'text-red-800',
         icon: IconLib.error.circle,
      },
   };
   return configs[props.variant];
});

const alertClasses = computed(() => {
   return [
      'flex gap-2 p-3 rounded-lg border text-sm',
      variantConfig.value.bg,
      variantConfig.value.border,
   ].join(' ');
});

const displayIcon = computed(() => props.icon || variantConfig.value.icon);
</script>

<template>
   <div :class="alertClasses" role="alert">
      <!-- Icon -->
      <Icon
         v-if="!hideIcon"
         :icon="displayIcon"
         class="size-4 flex-shrink-0 mt-0.5"
         :class="variantConfig.text"
      />

      <!-- Content -->
      <div class="flex-1 min-w-0">
         <p v-if="title" class="font-semibold" :class="variantConfig.text">
            {{ title }}
         </p>
         <p :class="[variantConfig.text, title ? 'mt-1' : '']">
            <slot>{{ message }}</slot>
         </p>
      </div>

      <!-- Dismiss button -->
      <button
         v-if="dismissible"
         type="button"
         class="flex-shrink-0 p-1 rounded-md transition-colors hover:bg-black/10"
         :class="variantConfig.text"
         aria-label="Dismiss"
         @click="emit('dismiss')"
      >
         <Icon icon="mdi:close" class="size-4" />
      </button>
   </div>
</template>
